import { Chip, Stack } from "@mui/material"

import { BRAND_FILL, BRAND_FILL_HOVER } from "../theme"

/**
 * The views somebody actually asks for, as one-click toggles.
 *
 * ── Why these are chips and not another select ──────────────────────────────
 *
 * "Assigned to me" and "Overdue" are not values of a field — they are the
 * questions people come to this page holding. Expressed as dropdowns they take
 * three clicks each and read as configuration; as chips they are one click and
 * read as what they are, which is a view.
 *
 * ── Why they stack rather than switch ───────────────────────────────────────
 *
 * They are independent booleans, not tabs. "Assigned to me AND overdue" is the
 * most useful question on the page and a tab strip cannot ask it. The cost is
 * that a combination can match nothing, which is why the empty state says the
 * filters are the reason rather than implying there is no work.
 *
 * ── Why the active one is filled rather than outlined ───────────────────────
 *
 * On a row of six, a border-weight difference is not a difference anybody sees
 * at a glance. Filled is unambiguous from across the desk, and a filter you
 * cannot tell is on is a filter that silently hides rows.
 *
 * ── Why the ink is white rather than measured ───────────────────────────────
 *
 * It used to be computed against whatever green was behind it, which on the
 * brand itself lands on near-black — correct by the numbers and wrong to look
 * at, because a toggled filter then reads as a highlighter mark rather than a
 * pressed control. So the ink is fixed at white and the green moves instead:
 * BRAND_FILL carries white at 4.75:1, and its hover step keeps it there under
 * the cursor, which is where the old 2.6:1 state lived.
 */
export default function QuickFilters({ items, values, onToggle }) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
      {items.map(({ key, label, icon: Icon }) => {
        const on = Boolean(values[key])
        return (
          <Chip
            key={key}
            label={label}
            icon={Icon ? <Icon sx={{ fontSize: "15px !important", color: "inherit !important" }} /> : undefined}
            variant={on ? "filled" : "outlined"}
            onClick={() => onToggle(key)}
            sx={
              on
                ? {
                    bgcolor: BRAND_FILL,
                    color: "#ffffff",
                    fontWeight: 600,
                    "&:hover": { bgcolor: BRAND_FILL_HOVER },
                    "& .MuiChip-icon": { color: "inherit" },
                  }
                : { fontWeight: 500 }
            }
            /* A pressed button, not a switch: `role="switch"` takes
               `aria-checked` and would make each chip announce as on/off in
               isolation, where what these are is a set of toggles over one
               list. Said out loud either way, because otherwise a screen
               reader reads six buttons whose state is only their colour. */
            aria-pressed={on}
          />
        )
      })}
    </Stack>
  )
}
